import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { Building2, MapPin, Mail, Phone, Globe, Image as ImageIcon, ArrowLeft, Loader } from 'lucide-react';
import AddressAutocomplete from '../AddressAutocomplete';

const CreateMosque = () => {
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    street: '',
    postcode: '',
    city: '',
    country: '',
    countryCode: '',
    email: '',
    phone: '',
    website: '',
    photo: '',
    prayerTimeSource: 'aladhan',
    latitude: '',
    longitude: '',
    calculationMethod: '3', // Muslim World League
    school: '0', // Shafi
    jumahEnabled: true,
    jumahTime: ''
  });

  const calculationMethods = [
    { value: '1', label: 'University of Islamic Sciences, Karachi' },
    { value: '2', label: 'Islamic Society of North America (ISNA)' },
    { value: '3', label: 'Muslim World League' },
    { value: '4', label: 'Umm Al-Qura University, Makkah' },
    { value: '5', label: 'Egyptian General Authority of Survey' },
    { value: '7', label: 'Institute of Geophysics, University of Tehran' },
    { value: '8', label: 'Gulf Region' },
    { value: '9', label: 'Kuwait' },
    { value: '10', label: 'Qatar' },
    { value: '11', label: 'Majlis Ugama Islam Singapura, Singapore' },
    { value: '12', label: 'Union Organization islamic de France' },
    { value: '13', label: 'Diyanet İşleri Başkanlığı, Turkey' },
    { value: '14', label: 'Spiritual Administration of Muslims of Russia' }
  ];

  const schools = [
    { value: '0', label: 'Shafi' },
    { value: '1', label: 'Hanafi' }
  ];

  const countryCodes = [
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'UK', name: 'United Kingdom' }, // catalogue/index contract uses UK (ISO GB)
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'TR', name: 'Turkey' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'AE', name: 'United Arab Emirates' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      street: addressData.street,
      postcode: addressData.postcode,
      city: addressData.city,
      country: addressData.country,
      countryCode: addressData.countryCode === 'GB' ? 'UK' : addressData.countryCode, // Places returns ISO GB
      latitude: addressData.latitude?.toString() || '',
      longitude: addressData.longitude?.toString() || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const createMosque = httpsCallable(functions, 'createMosque');
      const normalizedName = formData.name.trim();
      const normalizedEmail = formData.email.trim();
      const normalizedPhone = formData.phone.trim();
      const normalizedWebsite = formData.website.trim();
      if (!normalizedName) {
        setError('Mosque name is required');
        setLoading(false);
        return;
      }
      if (normalizedEmail && !EMAIL_REGEX.test(normalizedEmail)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }
      const latitude = parseFloat(formData.latitude);
      const longitude = parseFloat(formData.longitude);

      if (
        formData.prayerTimeSource === 'aladhan' &&
        (!Number.isFinite(latitude) || !Number.isFinite(longitude))
      ) {
        setError('Valid latitude and longitude are required when using Aladhan prayer times');
        setLoading(false);
        return;
      }

      const location = {
        street: formData.street,
        postcode: formData.postcode,
        city: formData.city,
        country: formData.country,
        countryCode: formData.countryCode,
        coordinates: formData.latitude && formData.longitude ? {
          lat: latitude,
          lng: longitude
        } : null
      };

      const aladhanSettings = formData.prayerTimeSource === 'aladhan' ? {
        calculationMethod: parseInt(formData.calculationMethod, 10),
        latitude: latitude,
        longitude: longitude,
        school: parseInt(formData.school, 10),
        midnightMode: 0
      } : null;

      const features = {
        jumahEnabled: formData.jumahEnabled,
        jumahTime: formData.jumahTime || null,
        announcementsEnabled: true,
        newsEnabled: false,
        calendarEnabled: false
      };

      const result = await createMosque({
        name: normalizedName,
        displayName: formData.displayName?.trim() || normalizedName,
        location,
        email: normalizedEmail,
        phone: normalizedPhone || null,
        website: normalizedWebsite || null,
        photo: formData.photo || null,
        prayerTimeSource: formData.prayerTimeSource,
        aladhanSettings,
        features
      });

      if (result.data.success) {
        navigate('/admin/mosques');
      }
    } catch (err) {
      console.error('Error creating mosque:', err);
      setError(err.message || 'Failed to create mosque');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/mosques')}
          className={`flex items-center gap-2 mb-4 text-sm transition-colors ${
            isDarkMode
              ? 'text-gray-400 hover:text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Mosques
        </button>
        <h1 className={`text-2xl font-bold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Add New Mosque
        </h1>
        <p className={`mt-1 text-sm ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          Create a new mosque and manage its prayer times
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mb-6 p-4 rounded-xl border ${
          isDarkMode
            ? 'bg-red-900/20 border-red-500/30 text-red-400'
            : 'bg-red-50 border-red-200 text-red-600'
        }`}>
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <Building2 className="w-5 h-5" />
            Basic Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Search Mosque Name *
              </label>
              <AddressAutocomplete
                onPlaceSelect={(data) => {
                  // Update mosque name
                  setFormData(prev => ({ ...prev, name: data.placeName || data.formattedAddress }));
                  // Update address fields
                  handleAddressSelect(data);
                }}
                initialValue={formData.name}
                placeholder="Start typing mosque name..."
                searchType="mosque"
              />
              <p className={`mt-1.5 text-xs ${
                isDarkMode ? 'text-gray-500' : 'text-gray-500'
              }`}>
                Search for mosque to auto-fill address details
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Mosque Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="e.g., Grote Moskee Amsterdam"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Display Name (optional)
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="Alternative display name"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <MapPin className="w-5 h-5" />
            Location
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Street Address *
              </label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="e.g., Kraaiennestlaan 1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Postcode *
                </label>
                <input
                  type="text"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                  placeholder="e.g., 1103 CL"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                  placeholder="e.g., Amsterdam"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Country *
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                  placeholder="e.g., Netherlands"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Country Code *
                </label>
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                >
                  <option value="">Select...</option>
                  {countryCodes.map(({ code, name }) => (
                    <option key={code} value={code}>{code} - {name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                  placeholder="e.g., 52.3676"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                  placeholder="e.g., 4.9041"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <Mail className="w-5 h-5" />
            Contact Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="contact@mosque.com (optional until handover)"
              />
              <p className={`mt-1.5 text-xs ${
                isDarkMode ? 'text-gray-500' : 'text-gray-500'
              }`}>
                Optional - Required when transferring to mosque admin
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="+31 20 1234567"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="https://mosque.com"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Photo URL
              </label>
              <input
                type="url"
                name="photo"
                value={formData.photo}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="https://example.com/photo.jpg"
              />
            </div>
          </div>
        </div>

        {/* Prayer Time Configuration */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Prayer Time Configuration
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Prayer Time Source
              </label>
              <select
                name="prayerTimeSource"
                value={formData.prayerTimeSource}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
              >
                <option value="aladhan">Aladhan API (Automatic)</option>
                <option value="custom">Custom (Manual Entry)</option>
              </select>
            </div>

            {formData.prayerTimeSource === 'aladhan' && (
              <>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Calculation Method
                  </label>
                  <select
                    name="calculationMethod"
                    value={formData.calculationMethod}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                      isDarkMode
                        ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                    } focus:outline-none`}
                  >
                    {calculationMethods.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    School of Jurisprudence (for Asr)
                  </label>
                  <select
                    name="school"
                    value={formData.school}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                      isDarkMode
                        ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                    } focus:outline-none`}
                  >
                    {schools.map(school => (
                      <option key={school.value} value={school.value}>
                        {school.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="pt-4 border-t border-gray-700">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="jumahEnabled"
                  checked={formData.jumahEnabled}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500"
                />
                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                  Enable Jumu'ah Prayer
                </span>
              </label>

              {formData.jumahEnabled && (
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Jumu'ah Time
                  </label>
                  <input
                    type="time"
                    name="jumahTime"
                    value={formData.jumahTime}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                      isDarkMode
                        ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                    } focus:outline-none`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/mosques')}
            className={`flex-1 px-6 py-3 rounded-xl transition-colors ${
              isDarkMode
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`flex-1 px-6 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 ${
              isDarkMode
                ? 'bg-emerald-500 text-black hover:bg-emerald-400 disabled:bg-emerald-500/50'
                : 'bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400'
            }`}
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Mosque'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateMosque;
